import { useTranslation } from 'react-i18next'
import { Link, useLoaderData } from 'react-router'
import { HeroBackdrop } from '#app/components/hero-backdrop.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { CardWave } from '#app/components/ui/card-wave.tsx'
import { EmptyState } from '#app/components/ui/empty-state.tsx'
import { glassControlClassName } from '#app/components/ui/glass.ts'
import { Reveal } from '#app/components/ui/reveal.tsx'
import { WordReveal } from '#app/components/ui/word-reveal.tsx'
import { getLocalizedField } from '#app/utils/blog-i18n.ts'
import { useLocalizedPath } from '#app/utils/i18n.ts'
import { cn, getShopImgSrc } from '#app/utils/misc.tsx'
import { getLayoutSeoData } from '#app/utils/seo.ts'
import {
	generateShopBreadcrumbJsonLd,
	generateShopMeta,
} from '#app/utils/shop-seo.ts'
import {
	getAvailableProducts,
	getShopCategories,
} from '#app/utils/shop.server.ts'
import { ProductCard } from './+shop-components/product-card.tsx'
import { type Route } from './+types/shop._index.ts'

export const handle = { i18n: 'common' }

const META_TITLE = 'Shop | Open Sourced'
const META_DESCRIPTION =
	'Ungoogled Phones und Linux Laptops, datenschutzfreundliche Geräte von Open Sourced.'

export const meta: Route.MetaFunction = ({ data, matches }) => {
	const { siteUrl } = getLayoutSeoData(matches)
	const title = data?.metaTitle ?? META_TITLE
	const description = data?.metaDescription ?? META_DESCRIPTION

	return [
		...generateShopMeta({
			title,
			description,
			siteUrl,
			lang: 'de',
			path: '/shop',
		}),
		{ 'script:ld+json': generateShopBreadcrumbJsonLd({ locale: 'de', siteUrl }) },
	]
}

export async function loader() {
	const [categories, products] = await Promise.all([
		getShopCategories(),
		getAvailableProducts(),
	])

	return {
		categories,
		products,
		metaTitle: META_TITLE,
		metaDescription: META_DESCRIPTION,
	}
}

export default function ShopIndex() {
	const { categories, products } = useLoaderData<typeof loader>()
	const { t } = useTranslation()
	const localePath = useLocalizedPath()

	const nonEmptyCategories = categories.filter((c) => c._count.products > 0)

	return (
		<>
			{/* Hero */}
			<section className="relative overflow-hidden pt-32 pb-24">
				<HeroBackdrop />

				<div className="relative z-10 container text-center">
					<h1 className="mx-auto max-w-3xl text-h2 md:text-h1 font-bold">
						<WordReveal text="Shop" wordClassName="text-gradient-brand" />
					</h1>
					<Reveal delay={100}>
						<p className="text-body-md text-muted-foreground md:text-body-lg mx-auto mt-6 max-w-2xl">
							Datenschutzfreundliche Geräte und Hosting, ohne
							Kompromisse.
						</p>
					</Reveal>

					{/* Category filters */}
					{nonEmptyCategories.length > 0 && (
						<nav
							aria-label="Shop-Kategorien"
							className="mx-auto mt-8 flex max-w-3xl flex-wrap justify-center gap-2"
						>
							{nonEmptyCategories.map((category) => (
								<Link
									key={category.id}
									to={`/de/shop/category/${category.slug}`}
									className={cn(
										glassControlClassName,
										'text-body-sm hover:border-primary/30 hover:bg-primary/5 hover:text-primary flex items-center gap-2 rounded-full border px-4 py-1.5 transition-colors whitespace-nowrap',
									)}
								>
									{category.imageObjectKey && (
										<img
											src={getShopImgSrc(category.imageObjectKey)}
											alt=""
											className="h-5 w-5 rounded-full object-cover"
										/>
									)}
									{getLocalizedField(category, 'name', 'de')}
								</Link>
							))}
						</nav>
					)}
				</div>
			</section>

			{/* Product grid */}
			<div className="container pt-16 pb-16">
				{products.length === 0 ? (
					<EmptyState
						icon="laptop"
						title={t('shop.catalog.empty.title')}
						description={t('shop.catalog.empty.description')}
						action={
							<Button asChild variant="outline" size="sm">
								<Link to={localePath('/contact')}>
									{t('cta.contact')}
								</Link>
							</Button>
						}
					/>
				) : (
					<CardWave className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
						{products.map((product) => (
							<ProductCard key={product.id} product={product} />
						))}
					</CardWave>
				)}
			</div>
		</>
	)
}
